"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import { Avatar as AvatarPrimitive } from "radix-ui";
import * as React from "react";
import { Profile } from "../../Icons";

export interface AvatarFallbackProps extends React.ComponentProps<typeof AvatarPrimitive.Fallback> {}
const Avatar = ({ ref, className, children, ...props }: AvatarProps) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn("relative flex size-10 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  >
    {children}
  </AvatarPrimitive.Root>
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

export interface AvatarImageProps extends React.ComponentProps<typeof AvatarPrimitive.Image> {}
const AvatarImage = ({ ref, className, ...props }: AvatarImageProps) => (
  <AvatarPrimitive.Image ref={ref} loading="lazy" className={cn("aspect-square size-full", className)} {...props} />
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export interface AvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root> {}
const AvatarFallback = ({ ref, className, children, ...props }: AvatarFallbackProps) => {
  let initials: string | undefined;
  if (typeof children === "string") {
    let words = children.toUpperCase().split(" ");
    if (words.length === 1) words = children.split("-");
    initials = words
      .map((word) => word[0])
      .join("")
      .toUpperCase();
    if (initials.length > 2) initials = initials.slice(0, 2);
    if (initials.length === 1) initials = children.toUpperCase().slice(0, 2);
  }
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex size-full items-center justify-center rounded-full bg-medium-gray font-semibold text-white",
        className,
      )}
      {...props}
    >
      {initials ?? children ?? <Profile className="size-6/10" />}
    </AvatarPrimitive.Fallback>
  );
};
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export interface AvatarGroupProps extends React.ComponentProps<"div"> {
  /** Maximum number of avatars to show before collapsing */
  max?: number;
}

const AvatarGroup = ({ className, children, max = 3, ...props }: AvatarGroupProps) => {
  // oxlint-disable-next-line react/no-react-children -- children must be inspected to render
  const childArray = React.Children.toArray(children);
  const hasMoreThanMax = childArray.length > max;
  const visibleChildren = (hasMoreThanMax ? childArray.slice(0, max - 1) : childArray).toReversed();
  const overflowCount = hasMoreThanMax ? childArray.length - max + 1 : 0;

  return (
    <div
      className={cn("flex flex-row-reverse items-center -space-x-7.5 space-x-reverse *:ring-1 *:ring-white", className)}
      {...props}
    >
      {overflowCount > 0 && (
        <Avatar>
          <AvatarFallback>+{overflowCount}</AvatarFallback>
        </Avatar>
      )}
      {visibleChildren}
    </div>
  );
};
AvatarGroup.displayName = "AvatarGroup";

export { Avatar, AvatarFallback, AvatarGroup, AvatarImage };
